import React from 'react';
import { useListForm } from '../../hooks/useListForm';
import { useSceneUpdate } from '../../hooks/useSceneUpdate';
import Conditionals from '../Conditionals';
import { getAllEndingsFromNovel } from '../../utils/endingsHelper';
import './InteractiveSceneBackground.css';
import CustomSelect from '../CustomSelect';

const INITIAL_DATA = { background_asset_id: '', priority: 1, conditionals: [] };

const InteractiveSceneBackground = ({
  novelId, sceneId, scene, setScene, novel, loading, setLoading, error, setError, isProduction = false
}) => {
  const { showForm, editingIndex, formData, setFormData, openAdd, openEdit, close } = useListForm(INITIAL_DATA);
  const { saveScene } = useSceneUpdate(novelId, sceneId, isProduction, setScene, setLoading);

  const handleEditBackgroundClick = (background, index) => {
    openEdit({
      background_asset_id: background.background_asset_id,
      priority: background.priority,
      conditionals: background.conditionals || []
    }, index);
  };

  const handleSaveBackground = async () => {
    if (!formData.background_asset_id) {
      setError('Выберите ресурс фона');
      return;
    }
    setError(null);
    const updatedBackgrounds = [...(scene?.backgrounds || [])];
    const newBackground = {
      background_asset_id: formData.background_asset_id,
      priority: parseInt(formData.priority),
      conditionals: formData.conditionals
    };
    if (editingIndex !== null) {
      updatedBackgrounds[editingIndex] = newBackground;
    } else {
      updatedBackgrounds.push(newBackground);
    }
    const success = await saveScene({ ...scene, backgrounds: updatedBackgrounds }, 'Фон успешно сохранен!');
    if (success) close();
  };

  const handleDeleteBackground = async (index) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот фон из сцены?')) return;
    const updatedBackgrounds = [...(scene?.backgrounds || [])];
    updatedBackgrounds.splice(index, 1);
    await saveScene({ ...scene, backgrounds: updatedBackgrounds }, 'Фон успешно удален!');
  };

  const getBackgroundAssetName = (id) => novel?.assets?.backgrounds?.find(a => a.id === id)?.name || 'Неизвестный фон';

  return (
    <div className="isbg-wrap">
      <div className="isbg-header">
        <h3 className="isbg-title">Фоны сцены</h3>
      </div>

      <div className="isbg-add-wrap">
        <button className="isbg-add-btn" onClick={openAdd} disabled={loading}>
          <span className="button-icon">➕</span>
          Добавить фон
        </button>
      </div>

      {error && <div className="isbg-error">{error}</div>}

      {showForm && (
        <div className="isbg-overlay">
          <div className="isbg-modal">
            <h3 className="isbg-modal-title">{editingIndex !== null ? 'Редактировать фон' : 'Добавить фон'}</h3>

            <div className="isbg-form-group">
              <label className="isbg-label">Ресурс фона:</label>
              <CustomSelect
                value={formData.background_asset_id}
                onChange={(e) => setFormData({ ...formData, background_asset_id: e.target.value })}
                className="app-select" disabled={loading} required
              >
                <option value="">Выберите фон</option>
                {novel?.assets?.backgrounds?.map((bg) => (
                  <option key={bg.id} value={bg.id}>{bg.name}</option>
                ))}
              </CustomSelect>
            </div>

            <div className="isbg-form-group">
              <label className="isbg-label">Приоритет:</label>
              <input
                type="number" value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="isbg-input" disabled={loading} min="1" required
              />
              <small className="isbg-help">Фоны с более высоким приоритетом будут отображаться при выполнении нескольких условий</small>
            </div>

            <div className="isbg-form-group">
              <label className="isbg-label">Условия:</label>
              <Conditionals
                flags={novel?.states?.flags || []}
                scales={novel?.states?.scales || []}
                endings={getAllEndingsFromNovel(novel)}
                value={formData.conditionals}
                onChange={(conditionals) => setFormData({ ...formData, conditionals })}
                disabled={loading}
              />
            </div>

            <div className="isbg-form-actions">
              <button type="button" onClick={handleSaveBackground} disabled={loading} className="isbg-btn-primary">
                {loading ? 'Загрузка...' : 'Сохранить'}
              </button>
              <button type="button" onClick={() => { close(); setError(null); }} disabled={loading} className="isbg-btn-cancel">
                Отменить
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="isbg-list">
        {scene?.backgrounds?.length > 0 ? (
          scene.backgrounds.map((background, index) => (
            <div key={index} className="isbg-card">
              <div className="isbg-card-head">
                <h4 className="isbg-card-name">{getBackgroundAssetName(background.background_asset_id)}</h4>
                <div className="isbg-card-actions">
                  <button className="isbg-btn-edit" onClick={() => handleEditBackgroundClick(background, index)} disabled={loading}>
                    Редактировать фон
                  </button>
                  <button className="isbg-btn-delete" onClick={() => handleDeleteBackground(index)} disabled={loading}>
                    Удалить
                  </button>
                </div>
              </div>
              <div className="isbg-card-body">
                <p><strong>Приоритет:</strong> {background.priority}</p>
                {background.conditionals?.length > 0 && (
                  <p><strong>Условия:</strong> {background.conditionals.length}</p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="isbg-empty">
            <p>В этой сцене пока нет фонов.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveSceneBackground;
